import { useEffect, useState } from 'react'
import Papa from 'papaparse'

type SubmissionSource = {
  id: string
  name: string
  arrived: string
  grade: string
  url: string
}

const SUBMISSION_SOURCES: SubmissionSource[] = [
  {
    id: '473-damages',
    name: '473 — Damages',
    arrived: 'Apr 22, 2026',
    grade: 'C-grade damages',
    url: 'https://cdn.magicpatterns.com/uploads/qrCgAprgiZmaQwUrxK4YsD/473_Damages_valued_upload.csv',
  },
  {
    id: '472-imperfects',
    name: '472 — Imperfects',
    arrived: 'Feb 15, 2026',
    grade: 'B-grade imperfects',
    url: 'https://cdn.magicpatterns.com/uploads/8MA2Kc9yfniH5hFHDUFdN6/472_Imperfects_vendor_receiving_with_estimates.csv',
  },
]

const IMAGE_COLUMNS = ['rollup ai image temp (from Products) (from Variants)', '🗑️Images for AI - TEMP']

const firstImage = (row: Record<string, string>) => {
  for (const column of IMAGE_COLUMNS) {
    const match = (row[column] ?? '').match(/https?:\/\/[^\s,)"']+/)
    if (match) return match[0]
  }
  return ''
}

export type SizeTier = 'Core' | 'Adjacent' | 'Tail'

export type Breakdown = { label: string; units: number; value?: number; tier?: SizeTier }

export type SubmissionLine = {
  name: string
  upc: string
  units: number
  msrp: number
  predict: number
  recovery: number
  category: string
  size: string
  image: string
}

export type NisoloSubmission = {
  id: string
  arrived: string
  name: string
  grade: string
  expected: number
  received: number
  progress: number
  lineItems: number
  msrp: number
  liquidationOffer: number
  estimatedRecovery: number
  lines: SubmissionLine[]
  categories: Breakdown[]
  sizes: Breakdown[]
  priceBands: Breakdown[]
  missingUpc: number
  missingPredict: number
  missingMsrp: number
  shortShipped: number
}

export type NisoloSubmissionData = {
  submissions: NisoloSubmission[]
  totalExpected: number
  totalReceived: number
  totalMsrp: number
  totalLiquidationOffer: number
  totalEstimatedRecovery: number
}

type CsvRow = Record<string, string>

const toNumber = (value: string | undefined) => {
  const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

const CATEGORY_RULES: Array<[string, RegExp]> = [
  ['Boots', /\b(boot|bootie|chelsea)\b/i],
  ['Sandals', /\b(sandal|slide|huarache)\b/i],
  ['Sneakers', /\b(sneaker|trainer)\b/i],
  ['Loafers & Mules', /\b(loafer|mule|slip[- ]?on)\b/i],
  ['Heels', /\b(heel|pump|wedge)\b/i],
  ['Flats & Oxfords', /\b(flat|oxford|derby|lace[- ]?up)\b/i],
  ['Bags', /\b(bag|tote|clutch|backpack|pouch|wallet)\b/i],
  ['Belts', /\bbelt\b/i],
  ['Accessories', /\b(hat|scarf|sock|jewel|earring|necklace|keychain|card case)\b/i],
  ['Apparel', /\b(shirt|tee|jacket|dress|pant|short|sweater)\b/i],
]

const categoryFor = (name: string) => {
  const match = CATEGORY_RULES.find(([, pattern]) => pattern.test(name))
  return match ? match[0] : 'Other footwear & goods'
}

const sizeFor = (name: string) => {
  const trailing = name.trim().match(/(?:^|[\s\-–|/])((?:[0-9]{1,2}(?:\.5)?)|(?:XXS|XS|S|M|L|XL|XXL|2XL|3XL|OS|One Size))\s*$/i)
  if (trailing) return trailing[1].toUpperCase()
  const embedded = name.match(/\bsize\s+([0-9]{1,2}(?:\.5)?|XXS|XS|S|M|L|XL|XXL)\b/i)
  return embedded ? embedded[1].toUpperCase() : 'Unsized'
}

const bandFor = (msrp: number) => {
  if (msrp <= 0) return 'No MSRP'
  if (msrp < 100) return 'Under $100'
  if (msrp < 150) return '$100 – $149'
  if (msrp < 200) return '$150 – $199'
  return '$200+'
}

const CORE_SIZES = ['7', '7.5', '8', '8.5', '9', '9.5', '10', 'M', 'L']
const ADJACENT_SIZES = ['6', '6.5', '10.5', '11', 'S', 'XL']

export const tierFor = (label: string): SizeTier => {
  if (CORE_SIZES.includes(label)) return 'Core'
  if (ADJACENT_SIZES.includes(label)) return 'Adjacent'
  return 'Tail'
}

const tally = (entries: Array<{ label: string; units: number; value: number }>) => {
  const map = new Map<string, { units: number; value: number }>()
  entries.forEach(({ label, units, value }) => {
    const existing = map.get(label) ?? { units: 0, value: 0 }
    map.set(label, { units: existing.units + units, value: existing.value + value })
  })
  return Array.from(map, ([label, totals]) => ({ label, units: totals.units, value: totals.value }))
    .filter((item) => item.units > 0)
    .sort((a, b) => b.units - a.units)
}

const sizeOrder = (label: string) => {
  const numeric = Number(label)
  if (Number.isFinite(numeric)) return numeric
  const scale = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', 'OS', 'ONE SIZE']
  const index = scale.indexOf(label)
  return index >= 0 ? 100 + index : 999
}

function buildSubmission(source: SubmissionSource, rows: CsvRow[]): NisoloSubmission {
  let expected = 0
  let received = 0
  let msrp = 0
  let liquidationOffer = 0
  let estimatedRecovery = 0
  let missingUpc = 0
  let missingPredict = 0
  let missingMsrp = 0
  let shortShipped = 0

  const lines: SubmissionLine[] = []

  rows.forEach((row) => {
    const name = (row['Name'] ?? '').trim()
    if (!name) return

    const units = toNumber(row['Net Received'])
    const invoiced = toNumber(row['# Items on Invoice'])
    const rowMsrp = toNumber(row['Base MSRP'])
    const rowPredict = toNumber(row['Predict'])

    expected += invoiced
    received += units
    msrp += rowMsrp * units
    liquidationOffer += toNumber(row['Liquidation Offer']) * units
    estimatedRecovery += rowPredict * units

    if (!(row['UPC'] ?? '').trim()) missingUpc += 1
    if (rowPredict <= 0) missingPredict += 1
    if (rowMsrp <= 0) missingMsrp += 1
    if (invoiced > units) shortShipped += invoiced - units

    lines.push({
      name,
      upc: (row['UPC'] ?? '').trim(),
      units,
      msrp: rowMsrp,
      predict: rowPredict,
      recovery: rowPredict * units,
      category: categoryFor(name),
      size: sizeFor(name),
      image: firstImage(row),
    })
  })

  return {
    id: source.id,
    arrived: source.arrived,
    name: source.name,
    grade: source.grade,
    expected,
    received,
    progress: expected > 0 ? Math.min(100, Math.round((received / expected) * 1000) / 10) : 100,
    lineItems: lines.length,
    msrp,
    liquidationOffer,
    estimatedRecovery,
    lines: lines.slice().sort((a, b) => b.recovery - a.recovery),
    categories: tally(lines.map((line) => ({ label: line.category, units: line.units, value: line.recovery }))),
    sizes: tally(lines.map((line) => ({ label: line.size, units: line.units, value: line.recovery })))
      .map((item) => ({ ...item, tier: tierFor(item.label) }))
      .sort((a, b) => sizeOrder(a.label) - sizeOrder(b.label)),
    priceBands: tally(lines.map((line) => ({ label: bandFor(line.msrp), units: line.units, value: line.recovery }))),
    missingUpc,
    missingPredict,
    missingMsrp,
    shortShipped,
  }
}

const sum = (submissions: NisoloSubmission[], pick: (submission: NisoloSubmission) => number) =>
  submissions.reduce((total, submission) => total + pick(submission), 0)

export function useNisoloSubmissions(enabled: boolean) {
  const [data, setData] = useState<NisoloSubmissionData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled || data) return

    let cancelled = false
    setLoading(true)
    setError(null)

    Promise.all(SUBMISSION_SOURCES.map(async (source) => {
      const response = await fetch(source.url)
      if (!response.ok) throw new Error('Unable to load the Nisolo submission files.')
      const text = await response.text()
      const parsed = Papa.parse<CsvRow>(text, { header: true, skipEmptyLines: true })
      return buildSubmission(source, parsed.data)
    }))
      .then((submissions) => {
        if (cancelled) return
        setData({
          submissions,
          totalExpected: sum(submissions, (item) => item.expected),
          totalReceived: sum(submissions, (item) => item.received),
          totalMsrp: sum(submissions, (item) => item.msrp),
          totalLiquidationOffer: sum(submissions, (item) => item.liquidationOffer),
          totalEstimatedRecovery: sum(submissions, (item) => item.estimatedRecovery),
        })
      })
      .catch((caught: Error) => {
        if (!cancelled) setError(caught.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [enabled, data])

  return { data, loading, error }
}
